/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import {
  AppstoreOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  LogoutOutlined,
  SafetyOutlined,
  SolutionOutlined,
  TagsOutlined,
  UserOutlined,
  PartitionOutlined,
} from '@ant-design/icons';
import { Grid } from 'antd';

import { AvatarAndName } from '@/components/AvatarAndName';
import { canPerformAction } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { useTheme } from '@/hooks/useTheme';
import { Menu } from '@/primitives/Menu';
import { Text } from '@/primitives/Text';
import { selectedTenantIDKey, useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { MenuItemConfig, MenuProps, buildMenu } from './builder';

const { useBreakpoint } = Grid;

export function AccountMenu({ tenantID, currentPath, currentUser }: MenuProps) {
  const { t } = useMimicTranslation('menu');
  const profile = useMgmtPlaneStateStore((state) => state.userProfile);
  const theme = useTheme();
  const setMenuOpen = useMgmtPlaneStateStore((state) => state.setMenuOpen);
  const screens = useBreakpoint();

  const menuMode = screens.sm ? 'vertical' : 'inline';

  const userMenuItem = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        marginTop: '4px',
        backgroundColor: 'transparent',
      }}
    >
      <AvatarAndName userInfo={profile} color={theme.antdComponents?.Menu?.itemColor} />
      <Text size="sm" style={{ marginLeft: '30px' }}>
        {profile?.email}
      </Text>
    </div>
  );

  const menu: MenuItemConfig[] = [
    {
      name: 'accountMenu',
      label: <AvatarAndName userInfo={profile} color={theme.antdComponents?.Menu?.itemColor} />,
      dataTestId: 'account-menu',

      items: [
        {
          name: 'user',
          icon: userMenuItem,
          label: '',
          dataTestId: 'user-menu',
          closeMenuOnClick: true,
        },
        {
          type: 'divider',
        } as unknown as MenuItemConfig,
        {
          name: 'apiTokens',
          icon: <KeyOutlined />,
          label: t('apiTokens'),
          path: `/tenants/${tenantID}/api-tokens`,
          dataTestId: 'api-tokens-menu',
          closeMenuOnClick: true,
        },
        {
          name: 'oauthClients',
          icon: <SafetyOutlined />,
          label: t('oauthClients'),
          path: `/tenants/${tenantID}/oauth-clients`,
          dataTestId: 'oauth-clients-menu',
          requiredRole: 'superadmin',
          closeMenuOnClick: true,
        },
        {
          name: 'm2mClients',
          icon: <PartitionOutlined />,
          label: t('m2mClients'),
          path: `/tenants/${tenantID}/m2m-clients`,
          dataTestId: 'm2m-clients-menu',
          requiredRole: 'superadmin',
          closeMenuOnClick: true,
        },
        {
          name: 'tags',
          icon: <TagsOutlined />,
          label: t('tags'),
          path: `/tenants/${tenantID}/tags`,
          dataTestId: 'tags-menu',
          closeMenuOnClick: true,
        },
        {
          name: 'subtenants',
          icon: <AppstoreOutlined />,
          label: t('subtenants'),
          path: `/tenants/${tenantID}/subtenants`,
          dataTestId: 'subtenants-menu',
          closeMenuOnClick: true,
        },
        {
          name: 'users',
          icon: <UserOutlined />,
          label: t('users'),
          path: `/tenants/${tenantID}/users`,
          dataTestId: 'users-menu',
          closeMenuOnClick: true,
        },
        {
          name: 'userActivity',
          icon: <SolutionOutlined />,
          label: t('userActivity'),
          path: `/tenants/${tenantID}/user-activity`,
          dataTestId: 'jobs-menu',
          requiredRole: 'superadmin',
          closeMenuOnClick: true,
        },
        {
          name: 'logout',
          icon: <LogoutOutlined />,
          label: t('logout'),
          onClick: () => {
            localStorage.removeItem(selectedTenantIDKey);
            window.location.href = `${window.mimicConfig.MIMIC_MGMT_PLANE_API}/api/auth/sign-out`;
          },
          danger: true,
          dataTestId: 'logout-menu',
        },
        {
          type: 'divider',
        } as unknown as MenuItemConfig,
        {
          name: 'system-info',
          icon: <InfoCircleOutlined />,
          label: t('systemInfo'),
          path: '/system-info',
          dataTestId: 'system-info',
          closeMenuOnClick: true,
        },
      ],
    },
  ];

  // Filter items by requiredRole
  const filteredMenu = menu.map((menuItem) => ({
    ...menuItem,
    items: menuItem.items?.filter((item) => !item.requiredRole || canPerformAction(item.requiredRole, currentUser)),
  }));

  const { items, defaultSelectedKeys } = buildMenu(filteredMenu, currentPath, setMenuOpen);
  return (
    <Menu key={currentPath} mode={menuMode} items={items} defaultSelectedKeys={defaultSelectedKeys} inlineIndent={10} />
  );
}
